import React from "react";
import { OtpArray } from '../../types';
import { TriggerToast } from "./Toaster";

interface OneTimePasswordProps {
  oneTimePassword: OtpArray;
  setOneTimePassword: React.Dispatch<React.SetStateAction<OtpArray>>
}

/**
 * OneTimePassword component
 *
 * This component renders an OTP input field. It is designed to work with a state context where the OTP is managed.
 *
 * @param {OneTimePasswordProps} props - The props for the OneTimePassword component.
 * @param {string[]} props.oneTimePassword - An array of 6 strings representing the OTP digits. This should be managed using a `useState` context.
 * @param {Function} props.setOneTimePassword - A setter function from the `useState` hook to update the OTP array.
 *
 * @returns JSX.Element - The `OneTimePassword` component.
 *
 * @note This component can be used in instances where an OTP (One-Time Password) is required.
 */

const OneTimePassword: React.FC<OneTimePasswordProps> = ({ oneTimePassword, setOneTimePassword }): React.ReactElement => {

  const handleOnInput = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const input = evt.target as HTMLInputElement;
    const dataIndex = parseInt(input.dataset.index || "0");
    const value = input.value; // Capture the current value

    if (value.length > 1) {
      setOneTimePassword((prevArray) => {
        const newArray: OtpArray = [...prevArray];
        const newValue = value[1]
        newArray[dataIndex] = String(newValue); // Update the array with the new value
        return newArray;
      });
    } else {
      setOneTimePassword((prevArray) => {
        const newArray: OtpArray = [...prevArray];
        newArray[dataIndex] = String(value); // Update the array with the new value
        return newArray;
      });
    }

    // Focus on the next input element if it exists
    const nextInput = input.nextElementSibling as HTMLInputElement | null;
    if (input.value.length >= 1 && nextInput && nextInput.tagName === 'INPUT') {
      nextInput.focus();
    }
  }

  const handleOnKeyDown = (evt: React.KeyboardEvent<HTMLInputElement>) => {
    const input = evt.target as HTMLInputElement;
    const dataIndex = parseInt(input.dataset.index || "0");

    switch (evt.key) {
      case 'Backspace':
        evt.preventDefault(); // Prevent default backspace behavior

        setOneTimePassword((prevArray) => {
          const newArray: OtpArray = [...prevArray];
          newArray[dataIndex] = ""; // Clear the current digit

          // Focus on the previous input element if it exists
          const prevInput = input.previousElementSibling as HTMLInputElement | null;
          if (input.value === "" && prevInput && prevInput.tagName === 'INPUT') {
            prevInput.focus();
          }

          return newArray;
        });
        break;

      case 'ArrowLeft': {
        // Focus on the previous input element if it exists
        const prevInputArrow = input.previousElementSibling as HTMLInputElement | null;
        if (prevInputArrow && prevInputArrow.tagName === 'INPUT') {
          prevInputArrow.focus();
        }
        evt.preventDefault();
        break;
      }

      case 'ArrowRight': {
        // Focus on the next input element if it exists
        const nextInputArrow = input.nextElementSibling as HTMLInputElement | null;
        if (nextInputArrow && nextInputArrow.tagName === 'INPUT') {
          nextInputArrow.focus();
        }
        evt.preventDefault();
        break;
      }

      default:
        break;
    }
  }

  const handlePaste = (evt: React.ClipboardEvent<HTMLInputElement>) => {
    evt.preventDefault();
    const { clipboardData } = evt;
    const pastedText: string = clipboardData.getData("text")
    const pastedTextArray = pastedText.split('')
    if (pastedTextArray.length === 4 && pastedTextArray.every(item => !isNaN(Number(item)) && item.trim() !== '')) {
      setOneTimePassword(pastedTextArray as [string, string, string, string])
    } else {
      TriggerToast("You can't paste that here. Make sure the format is 4 digit numeric.", undefined, 'error')
    }
  }

  return (
    <div className="w-full text-center">
      <label htmlFor="ui-panel-login_verification-code_input" className="block">
        Verification Code
      </label>
      <span className="text-xs label-sub-text inline-block mb-[16px]">Enter the 4-digit code sent to the email you provided.</span>
      <div className="verification-code-blocks inline-flex w-full gap-[8px] justify-center">
        {oneTimePassword.map((digit: string, index: number) => (
          <input
            className="verification-code-block w-auto max-w-[36px] text-center h-[40px]"
            key={index}
            onInput={handleOnInput}
            onPaste={handlePaste}
            onKeyDown={handleOnKeyDown}
            data-index={index.toString()}
            type="text"
            value={digit}
            inputMode="numeric"
            pattern="[0-9]"
          />
        ))}
      </div>
    </div>
  );
};

export default OneTimePassword;
